/**
 * DeliverableMilestones — the project's milestones, read off its proposal.
 *
 * A milestone is one fact with two faces: a delivery lands, and a payment comes
 * due. This section is the delivery face; the Payments tab is the money face;
 * both are generated from the same quote (backend services/proposal_milestones),
 * so they cannot disagree. Nothing here is typed in by hand — that is what used
 * to leave the project page claiming a set of deliverables the proposal never
 * mentioned.
 *
 * Ticking a milestone off is what brings its payment due: the payment milestone
 * carries due_rule_type "OnMilestoneCompletion" pointing back at this row.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { ProgramSection, sectionActionClass } from "./ProgramSection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { apiClient } from "../../App";
import { toast } from "sonner";

// Payment status → pill. Same vocabulary as the Payments tab so one project
// never describes the same milestone two ways.
const PAYMENT_TONE = {
  Scheduled: "bg-secondary text-muted-foreground",
  SubmittedToFinance: "bg-blue-100 text-blue-700",
  InvoiceRequested: "bg-blue-100 text-blue-700",
  InvoiceSent: "bg-indigo-100 text-indigo-700",
  Invoiced: "bg-indigo-100 text-indigo-700",
  PartiallyPaid: "bg-amber-100 text-amber-700",
  Paid: "bg-green-100 text-green-700",
  Overdue: "bg-red-100 text-red-700",
  Returned: "bg-orange-100 text-orange-700",
  Waived: "bg-zinc-100 text-zinc-500",
};

const PAYMENT_LABEL = {
  Scheduled: "Payment scheduled",
  SubmittedToFinance: "Submitted to finance",
  InvoiceRequested: "Invoice requested",
  InvoiceSent: "Invoice sent",
  Invoiced: "Invoiced",
  PartiallyPaid: "Part paid",
  Paid: "Paid",
  Overdue: "Payment overdue",
  Returned: "Returned to PM",
  Waived: "Waived",
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

export function DeliverableMilestones({ programId, onProgressChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(`/programs/${programId}/proposal-milestones`);
      setData(res.data);
    } catch (e) {
      console.error("Failed to fetch proposal milestones", e);
    }
    setLoading(false);
  }, [programId]);

  useEffect(() => {
    let mounted = true;
    (async () => { if (mounted) await fetchData(); })();
    return () => { mounted = false; };
  }, [fetchData]);

  const runSync = async (replaceExisting) => {
    setBusy(true);
    try {
      const res = await apiClient.post(`/programs/${programId}/proposal-milestones/sync`, {
        replace_existing: !!replaceExisting,
      });
      const r = res.data;
      const created = r.deliverables_created || 0;
      const updated = r.deliverables_updated || 0;
      toast.success(
        `Synced from proposal — ${created} added, ${updated} updated` +
          (r.payments_removed ? `, ${r.payments_removed} stale payment row(s) removed` : "")
      );
      if (r.payments_kept_with_money) {
        toast.info(
          `${r.payments_kept_with_money} payment row(s) already have money recorded, so they were kept. Review them on the Payments tab.`
        );
      }
      await fetchData();
      if (onProgressChange) onProgressChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sync from proposal");
    }
    setBusy(false);
    setConfirmReplace(false);
  };

  const toggleComplete = async (row) => {
    if (!row.milestone_id) {
      toast.error("Sync from the proposal first");
      return;
    }
    try {
      await apiClient.patch(`/milestones/${row.milestone_id}`, { is_complete: !row.is_complete });
      await fetchData();
      if (onProgressChange) onProgressChange();
    } catch (e) {
      toast.error("Failed to update milestone");
    }
  };

  const approveSchedule = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/programs/${programId}/payments/approve-prepared`);
      toast.success("Payment schedule approved");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve schedule");
    }
    setBusy(false);
  };

  // Clearing the old rows IS a re-sync with replace_existing, so there is only
  // one code path that ever removes them.
  const clearLegacy = () => runSync(true);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border p-6">
        <div className="h-32 animate-pulse rounded-xl bg-secondary" />
      </div>
    );
  }

  const rows = data?.rows || [];
  const legacy = data?.manual_milestones || [];
  const synced = rows.some((r) => r.in_sync);

  return (
    <ProgramSection
      title="Deliverable Milestones"
      data-testid="deliverable-milestones"
      action={
        <div className="flex items-center gap-1">
          <Link
            to={`/programs/${programId}?tab=payments`}
            className={`${sectionActionClass} inline-flex items-center gap-1`}
            data-testid="milestones-payment-tracker-link"
          >
            Payment tracker
            <ArrowUpRight className="h-3 w-3" />
          </Link>
          {data?.has_quote && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => runSync(false)}
              className={sectionActionClass}
              data-testid="sync-proposal-milestones"
            >
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
              {synced ? "Re-sync" : "Sync from proposal"}
            </Button>
          )}
        </div>
      }
    >
      {/* No proposal linked — say what to do about it rather than showing an
          empty list that looks like a project with no deliverables. */}
      {!data?.has_quote ? (
        <div className="space-y-2">
          <p className="text-[13px] text-muted-foreground">
            This project isn't linked to the proposal it was priced from, so its milestones can't be
            pulled in.
          </p>
          <Link to="/sales/quote-links" className="inline-flex items-center gap-1 text-[13px] font-medium text-foreground hover:underline">
            Link its quote
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <>
          {/* The proposal's schedule is arithmetic; a human confirms it matches
              what was sold before finance bills against it. */}
          {data.needs_approval && (
            <div
              className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-3 py-2.5"
              data-testid="schedule-approval-banner"
            >
              <p className="text-[13px] text-foreground">
                Payment schedule prepared from the proposal — review it and approve.
              </p>
              {data.can_view_amounts ? (
                <Button size="sm" disabled={busy} onClick={approveSchedule} className="h-8 shrink-0 rounded-full px-3 text-[13px]" data-testid="approve-schedule">
                  <Check className="mr-1 h-3.5 w-3.5" />
                  Approve
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">Finance approves this.</span>
              )}
            </div>
          )}

          {rows.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              The linked proposal has no priced payment plan, so there are no milestones to show.
            </p>
          ) : (
            <div className="space-y-1.5">
              {rows.map((row) => (
                <div
                  key={row.key}
                  className={`flex items-start gap-3 rounded-xl border border-border px-3 py-2.5 ${
                    row.is_complete ? "opacity-70" : ""
                  }`}
                  data-testid={`proposal-milestone-${row.key}`}
                >
                  <Checkbox
                    checked={row.is_complete}
                    disabled={!row.milestone_id}
                    onCheckedChange={() => toggleComplete(row)}
                    className="mt-0.5"
                    data-testid={`milestone-checkbox-${row.key}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className={`text-[13px] font-medium text-foreground ${row.is_complete ? "line-through" : ""}`}>
                        {row.gate_label}
                      </p>
                      {!row.gated_on_delivery && (
                        <Badge variant="secondary" className="text-[10px]">On signature</Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {row.title}
                      {/* The percentage is the PROPOSAL's share. On a row that
                          was billed at a different figure it would contradict
                          the amount shown, so it is left off. */}
                      {row.pct && !row.amount_locked ? ` · ${row.pct}%` : ""}
                    </p>
                    {/* The proposal's explanation of the milestone is a full
                        paragraph — clamped so the section stays scannable, in
                        full on hover. */}
                    {row.note && (
                      <p
                        className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground/80"
                        title={row.note}
                      >
                        {row.note}
                      </p>
                    )}
                    {row.due_date && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarIcon className="h-3 w-3" />
                        {row.due_date}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {row.amount != null && (
                      <span className="text-[13px] font-medium tabular-nums text-foreground">
                        {/* An adopted row that was already invoiced keeps the
                            figure that was billed, which can differ from what
                            the proposal recomputes today. Show what is actually
                            scheduled, and flag the difference. */}
                        {formatCurrency(row.amount_locked && row.scheduled_amount != null ? row.scheduled_amount : row.amount)}
                      </span>
                    )}
                    {row.amount_locked && row.scheduled_amount != null && row.amount != null && (
                      <span
                        className="text-[10.5px] text-[#B96A43]"
                        title={`The proposal works out to ${formatCurrency(row.amount)} today, but this milestone was already billed at ${formatCurrency(row.scheduled_amount)}. The billed figure stands.`}
                      >
                        as billed · proposal {formatCurrency(row.amount)}
                      </span>
                    )}
                    {row.payment_status && (
                      <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-medium ${PAYMENT_TONE[row.payment_status] || PAYMENT_TONE.Scheduled}`}>
                        {PAYMENT_LABEL[row.payment_status] || row.payment_status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* What the percentages are a percentage OF — a project with a
              marketing campaign or a platform subscription bills those on their
              own ledgers, so these rows are not the whole contract. */}
          {data.plan?.scope_label && rows.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {data.plan.plan_key} plan on the {data.plan.scope_label}
              {data.can_view_amounts && data.plan.one_time_scope
                ? ` (${formatCurrency(data.plan.one_time_scope)})`
                : ""}
              . Recurring platform and marketing fees bill separately.
            </p>
          )}

          {/* The old hand-typed per-asset rows. Shown, counted, and removable —
              but never removed on this section's own initiative. */}
          {legacy.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <button
                onClick={() => setShowLegacy((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                data-testid="toggle-legacy-milestones"
              >
                {showLegacy ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {legacy.length} older hand-entered milestone{legacy.length === 1 ? "" : "s"}
              </button>
              {showLegacy && (
                <div className="mt-2 space-y-1">
                  {legacy.map((m) => (
                    <p key={m.id} className="truncate text-xs text-muted-foreground">
                      {m.is_complete ? "✓ " : "· "}
                      {m.title}
                    </p>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirmReplace(true)}
                    className="mt-1 h-7 rounded-full px-2.5 text-xs text-destructive hover:bg-destructive/10"
                    data-testid="clear-legacy-milestones"
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Clear them
                  </Button>
                </div>
              )}
            </div>
          )}

          {data.stale_payment_count > 0 && (
            <p className="mt-3 text-xs text-[#B96A43]" data-testid="stale-payment-warning">
              {data.stale_payment_count} payment row{data.stale_payment_count === 1 ? "" : "s"} on this
              project didn't come from the proposal
              {data.stale_payments_with_money > 0
                ? ` (${data.stale_payments_with_money} with money recorded, which will be kept)`
                : ""}
              . Clearing the older milestones removes the unpaid ones.
            </p>
          )}
        </>
      )}

      <Dialog open={confirmReplace} onOpenChange={setConfirmReplace}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear hand-entered milestones?</DialogTitle>
            <DialogDescription>
              This removes the {legacy.length} milestone{legacy.length === 1 ? "" : "s"} that didn't come
              from the proposal, and any payment rows on this project that the proposal didn't create.
              Payment rows with money already recorded against them are kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReplace(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={busy} onClick={clearLegacy} data-testid="confirm-clear-legacy">
              Clear and re-sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProgramSection>
  );
}
